// Eingefügtes HTML (Word/Outlook, Excel, Google Docs, Webseiten, andere Mails) auf das reduzieren, was der
// Email-Editor darstellen kann: Struktur (Absätze, Listen, Tabellen, Links) + fett/kursiv/unterstrichen/
// durchgestrichen, Textfarbe, Hervorhebung, Ausrichtung. Schriftart und -größe werden an den Hausstil angeglichen,
// sonst landen unsichtbare Formatierungen in der Mail (Klassen aus <style>, mso-*, fremde Fonts).
// Die Erkennung der Word-Listen folgt roosterjs (Editor von Outlook Web), lib/paste/WordDesktop.

import { applyStyleRules } from '@clientSpecific/email/template/inlineQuoteStyles';
import { DEFAULT_FONT_SIZE, FONT_FAMILY, isHouseFont, normalizeBackground, normalizeColor, normalizeFontSize } from './houseStyle';

const REMOVE_WITH_CONTENT = ['script', 'style', 'meta', 'link', 'title', 'head', 'xml', 'object', 'embed', 'iframe', 'frame',
    'form', 'input', 'button', 'select', 'textarea', 'svg', 'canvas', 'video', 'audio', 'noscript', 'template', 'map', 'area'];
const BLOCK_CONTAINERS = ['div', 'section', 'article', 'header', 'footer', 'main', 'aside', 'nav', 'center', 'address',
    'blockquote', 'figure', 'figcaption', 'dl', 'dt', 'dd', 'pre', 'form', 'fieldset', 'legend', 'details', 'summary'];
const BLOCK_TAGS = ['p', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', ...BLOCK_CONTAINERS];
const MARK_TAGS = {b: 'strong', strong: 'strong', i: 'em', em: 'em', cite: 'em', dfn: 'em', u: 'u', ins: 'u', s: 's', strike: 's', del: 's'};
const KEEP_TAGS = ['p', 'br', 'span', 'a', 'img', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'strong', 'em', 'u', 's'];
const SAFE_LINK = /^(https?:|mailto:|tel:)/i;
// data:-Bilder (aus Word-RTF, Screenshots, anderen Webmails) lädt der Editor danach auf den Server
const SAFE_IMAGE = /^(https?:\/\/|\/(?!\/)|data:image\/(png|jpe?g|gif);base64,)/i;

// "Ursprüngliche Formatierung beibehalten": Schriftart/-größe der Quelle bleiben (auch aus <style>-Klassen)
let keepFormatting = false;

export function detectPasteSource(html) {
    if (/data-pm-slice/.test(html)) {
        return 'editor';
    }
    if (/ProgId"?\s+content="?Excel\.Sheet|urn:schemas-microsoft-com:office:excel/i.test(html)) {
        return 'excel';
    }
    if (/ProgId"?\s+content="?Word\.Document|urn:schemas-microsoft-com:office:word|class="?Mso|mso-[a-z-]+\s*:/i.test(html)) {
        return 'word';
    }
    if (/id="?docs-internal-guid/i.test(html)) {
        return 'googleDocs';
    }
    return 'html';
}

export default function cleanPastedHtml(html, options = {}) {
    if (!html) {
        return '';
    }
    // Kopie innerhalb des Editors: ist schon im Schema, Schriftgrößen sollen dabei erhalten bleiben
    if (detectPasteSource(html) === 'editor') {
        return html;
    }

    keepFormatting = !!options.keepFormatting;
    const doc = new DOMParser().parseFromString(markWordListMarkers(stripConditionalComments(html)), 'text/html');
    const body = doc.body;
    preserveLayoutTables(doc);
    if (keepFormatting) {
        applyStyleRules(doc);
    }

    removeComments(body);
    markParagraphSpacing(doc, detectPasteSource(html));
    convertWordLists(body);
    unwrapGoogleDocsWrapper(body);
    cleanChildren(body);
    normalizeBlocks(body);
    removeEmptyInlines(body);

    removeBlockWhitespace(body);
    return body.innerHTML.trim();
}

// Zeilenumbrüche/Leerzeichen zwischen Blöcken (zwischen <li>, <tr>, <p>) sind kein Inhalt
function removeBlockWhitespace(root) {
    const containers = [root, ...root.querySelectorAll('ul, ol, table, thead, tbody, tfoot, tr')];
    containers.filter(container => !container.closest('[data-twig]')).forEach(container => {
        Array.from(container.childNodes).forEach(node => {
            if (node.nodeType === 3 && /^[ \t\r\n]*$/.test(node.textContent)) {
                node.remove();
            }
        });
    });
}

// Tabellen kommen exakt an (egal woher): als Block (HtmlBlock in extensions.js), nur ohne Office-Ballast.
// Styles aus Klassen-CSS (Outlook, Word, Excel) werden vorher in die Elemente geschrieben.
const EXACT_TABLE_ATTRIBUTES = ['style', 'width', 'height', 'colspan', 'rowspan', 'align', 'valign', 'bgcolor', 'border',
    'cellspacing', 'cellpadding', 'href', 'src', 'alt'];

function layoutTables(doc) {
    return Array.from(doc.body.querySelectorAll('table')).filter(table => !table.parentElement.closest('table'));
}

function preserveLayoutTables(doc) {
    const originals = layoutTables(doc);
    if (!originals.length) {
        return;
    }
    // Klassen-CSS nur für die Tabellen auflösen (auf einer Kopie), der restliche Text folgt dem gewählten Modus
    const styledDoc = doc.cloneNode(true);
    applyStyleRules(styledDoc);
    const styled = layoutTables(styledDoc);
    originals.forEach((table, index) => {
        const exact = doc.importNode(styled[index] || table, true);
        sanitizeExactTable(exact);
        if (!keepFormatting) {
            mergeTableFonts(exact);
        }
        exact.setAttribute('data-email-block', '1');
        table.parentNode.replaceChild(exact, table);
    });
}

function sanitizeExactTable(table) {
    removeComments(table);
    Array.from(table.querySelectorAll('*')).reverse().forEach(el => {
        const tag = el.tagName.toLowerCase();
        const style = parseStyle(styleOf(el));
        if (tag.includes(':')) {
            if (tag === 'o:p') {
                unwrap(el);
            } else {
                el.remove();
            }
            return;
        }
        if (REMOVE_WITH_CONTENT.includes(tag) || style.display === 'none' || style.visibility === 'hidden' || style['mso-hide'] === 'all') {
            el.remove();
            return;
        }
        if (tag === 'a' && !SAFE_LINK.test((el.getAttribute('href') || '').trim())) {
            unwrap(el);
            return;
        }
        if (tag === 'img' && !SAFE_IMAGE.test((el.getAttribute('src') || '').trim())) {
            el.remove();
            return;
        }
        cleanExactAttributes(el);
    });
    cleanExactAttributes(table);
}

// "Formatierung zusammenführen": Layout der Tabelle bleibt (Breiten, Linien, Hintergründe, Farben, fett …),
// Schriftart/-größe werden wie beim übrigen Text die Hausschrift. Jede Zelle bekommt sie ausdrücklich, weil
// Outlook (Word-Engine) Schrift nicht in Tabellen vererbt. Newsletter (Medium & Co.) bestehen ganz aus Tabellen.
function mergeTableFonts(table) {
    // Überschriften wie außerhalb von Tabellen: fetter Absatz in Hausschrift statt großer Überschrift
    Array.from(table.querySelectorAll('h1, h2, h3, h4, h5, h6')).forEach(heading => {
        const paragraph = rename(heading, 'p');
        paragraph.setAttribute('style', [paragraph.getAttribute('style'), 'margin:0', 'font-weight:bold'].filter(Boolean).join(';'));
    });
    [table, ...table.querySelectorAll('*')].forEach(el => {
        if (el.tagName === 'FONT') {
            el.removeAttribute('face');
            el.removeAttribute('size');
        }
        const style = (el.getAttribute('style') || '').split(';')
            .map(item => item.trim())
            .filter(item => item && !/^(font-family|font-size|font|line-height)\s*:/i.test(item));
        if (el.tagName === 'TD' || el.tagName === 'TH') {
            style.unshift(`font-family:${FONT_FAMILY}`, `font-size:${DEFAULT_FONT_SIZE}`);
        }
        if (style.length) {
            el.setAttribute('style', style.join(';'));
        } else {
            el.removeAttribute('style');
        }
    });
}

function cleanExactAttributes(el) {
    Array.from(el.attributes).forEach(attribute => {
        if (!EXACT_TABLE_ATTRIBUTES.includes(attribute.name.toLowerCase())) {
            el.removeAttribute(attribute.name);
        }
    });
    const style = (el.getAttribute('style') || '').split(';')
        .map(item => item.trim())
        .filter(item => item && !/^(mso-|tab-stops|page\s*:|behavior)/i.test(item) && !/mso-/i.test(item.split(':').slice(1).join(':')))
        .join(';');
    if (style) {
        el.setAttribute('style', style);
    } else {
        el.removeAttribute('style');
    }
}

// Absatzabstand der Quelle als Leerzeile übernehmen (wie Outlook beim Einfügen): Webseiten/Webmail haben an <p>,
// Listen und Überschriften standardmäßig eine Zeile Abstand (margin 1em), Outlook-Absätze "mso-margin-*-alt:auto".
// Word/Outlook-Text mit Klasse MsoNormal hat keinen Abstand. Im Editor selbst sind Absätze abstandslos (margin:0).
const SPACED_TAGS = ['p', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'dl', 'pre'];

function hasMargin(value) {
    return !!value && !/^(0|0?\.0+)(px|pt|cm|mm|em|rem|in|%)?$/i.test(value.trim()) && value.trim() !== 'auto';
}

// Abstand steht oft nur im Klassen-CSS (Outlook: p {mso-margin-top-alt:auto}) → auf einer Kopie mit aufgelösten
// Regeln ablesen und am Original markieren (gleiche Reihenfolge der Elemente)
function markParagraphSpacing(doc, source) {
    const styledDoc = doc.cloneNode(true);
    const hasRules = applyStyleRules(styledDoc);
    const originals = Array.from(doc.body.querySelectorAll(SPACED_TAGS.join(',')));
    const styled = hasRules ? Array.from(styledDoc.body.querySelectorAll(SPACED_TAGS.join(','))) : originals;
    originals.forEach((el, index) => markSpacing(el, styled[index] || el, source));
}

function markSpacing(el, styledEl, source) {
    if (el.closest('li, td, th, [data-email-block]') && !el.matches('ul, ol')) {
        return;
    }
    if (el.parentElement && el.parentElement.closest('li, td, th')) {
        return;
    }
    const style = parseStyle(styleOf(styledEl));
    const inline = parseStyle(styleOf(el));
    let top = style['margin-top'];
    let bottom = style['margin-bottom'];
    if (style.margin) {
        const parts = style.margin.split(/\s+/);
        top = top || parts[0];
        bottom = bottom || parts[2] || parts[0];
    }
    // "auto"-Abstand von Outlook: am Element selbst, oder aus der Tag-Regel nur bei Absätzen ohne Klasse
    // (p.MsoNormal setzt margin:0 – der Abstand aus "p {mso-margin-top-alt:auto}" gilt dort nicht)
    const msoAuto = side => inline[`mso-margin-${side}-alt`] === 'auto' || (!el.className && style[`mso-margin-${side}-alt`] === 'auto');
    const specified = top !== undefined || bottom !== undefined || style['mso-margin-top-alt'] || style['mso-margin-bottom-alt'];
    // ohne Angabe: Browser-Standard (Abstand) – außer bei Office-Quellen, dort regelt die Klasse (MsoNormal: 0)
    const byDefault = !specified && (source === 'html' || source === 'googleDocs') && !el.className;
    if (hasMargin(top) || msoAuto('top') || byDefault) {
        el.setAttribute('data-space-before', '1');
    }
    if (hasMargin(bottom) || msoAuto('bottom') || byDefault) {
        el.setAttribute('data-space-after', '1');
    }
}

function applyParagraphSpacing(root) {
    const isBlank = el => el && el.tagName === 'P' && !el.textContent.replace(/[\s\u00a0]/g, '') && !el.querySelector('img, br');
    const children = Array.from(root.children);
    children.forEach((current, index) => {
        const previous = children[index - 1];
        if (previous && !isBlank(previous) && !isBlank(current)
            && (previous.hasAttribute('data-space-after') || current.hasAttribute('data-space-before'))) {
            root.insertBefore(root.ownerDocument.createElement('p'), current);
        }
    });
    root.querySelectorAll('[data-space-before], [data-space-after]').forEach(el => {
        el.removeAttribute('data-space-before');
        el.removeAttribute('data-space-after');
    });
}

// Word: Aufzählungszeichen steckt in <!--[if !supportLists]-->…<!--[endif]--> bzw. <![if !supportLists]>…<![endif]>
function markWordListMarkers(html) {
    return html
        .replace(/<!--\[if !supportLists\]-->([\s\S]*?)<!--\[endif\]-->/gi, '<span data-word-list-marker="1">$1</span>')
        .replace(/<!\[if !supportLists\]>([\s\S]*?)<!\[endif\]>/gi, '<span data-word-list-marker="1">$1</span>');
}

function stripConditionalComments(html) {
    return html
        .replace(/<!--\[if (?!!supportLists)[\s\S]*?<!\[endif\]-->/gi, '')
        .replace(/<!\[if (?!!supportLists)[^\]]*\]>/gi, '')
        .replace(/<!\[endif\]>/gi, '')
        .replace(/<!--StartFragment-->|<!--EndFragment-->/gi, '');
}

function removeComments(root) {
    const walker = root.ownerDocument.createTreeWalker(root, 128 /* NodeFilter.SHOW_COMMENT */);
    const comments = [];
    while (walker.nextNode()) {
        comments.push(walker.currentNode);
    }
    comments.forEach(node => node.remove());
}

function styleOf(el) {
    return (el.getAttribute && el.getAttribute('style')) || '';
}

function wordListInfo(el) {
    const match = styleOf(el).match(/mso-list\s*:\s*(l\d+)\s+level(\d+)/i);
    return match ? {list: match[1], level: parseInt(match[2], 10)} : null;
}

function markerText(el) {
    const marker = el.querySelector('[data-word-list-marker], span[style*="mso-list"][style*="Ignore"], span[style*="mso-list:Ignore"]');
    return marker ? marker.textContent.replace(/\u00a0/g, ' ').trim() : '';
}

function isOrderedMarker(text) {
    return /^(\(?[0-9]+[.)]|\(?[a-zA-Z][.)]|\(?[ivxlcdmIVXLCDM]+[.)])/.test(text);
}

function convertWordLists(root) {
    const doc = root.ownerDocument;
    const items = Array.from(root.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div')).filter(el => wordListInfo(el));
    const handled = new Set();

    items.forEach(first => {
        if (handled.has(first)) {
            return;
        }
        // zusammenhängende Absätze mit mso-list bilden eine Liste
        const run = [first];
        let next = first.nextElementSibling;
        while (next && wordListInfo(next)) {
            run.push(next);
            next = next.nextElementSibling;
        }

        const rootList = {element: null, level: 0};
        const stack = [];
        run.forEach(p => {
            handled.add(p);
            const info = wordListInfo(p);
            const tag = isOrderedMarker(markerText(p)) ? 'ol' : 'ul';
            p.querySelectorAll('[data-word-list-marker], span[style*="mso-list"]').forEach(marker => {
                if (/Ignore/i.test(styleOf(marker)) || marker.hasAttribute('data-word-list-marker')) {
                    marker.remove();
                }
            });

            while (stack.length && stack[stack.length - 1].level > info.level) {
                stack.pop();
            }
            if (!stack.length || stack[stack.length - 1].level < info.level) {
                const list = doc.createElement(tag);
                if (stack.length) {
                    const parentItem = stack[stack.length - 1].element.lastElementChild;
                    (parentItem || stack[stack.length - 1].element).appendChild(list);
                } else {
                    rootList.element = list;
                }
                stack.push({element: list, level: info.level});
            }

            const li = doc.createElement('li');
            const style = styleOf(p);
            if (style) {
                li.setAttribute('style', style);
            }
            while (p.firstChild) {
                li.appendChild(p.firstChild);
            }
            stack[stack.length - 1].element.appendChild(li);
        });

        first.parentNode.insertBefore(rootList.element, first);
        run.forEach(p => p.remove());
    });

    root.querySelectorAll('[data-word-list-marker]').forEach(marker => marker.remove());
}

// Google Docs packt alles in <b style="font-weight:normal" id="docs-internal-guid-…">
function unwrapGoogleDocsWrapper(root) {
    root.querySelectorAll('b[id^="docs-internal-guid"]').forEach(unwrap);
}

function unwrap(el) {
    const parent = el.parentNode;
    if (!parent) {
        return;
    }
    while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
}

function rename(el, tag) {
    const replacement = el.ownerDocument.createElement(tag);
    Array.from(el.attributes).forEach(attr => replacement.setAttribute(attr.name, attr.value));
    while (el.firstChild) {
        replacement.appendChild(el.firstChild);
    }
    el.parentNode.replaceChild(replacement, el);
    return replacement;
}

function cleanChildren(parent) {
    Array.from(parent.childNodes).forEach(node => {
        if (node.nodeType === 1 && node.hasAttribute('data-email-block')) {
            return;
        }
        if (node.nodeType === 1) {
            cleanElement(node);
        } else if (node.nodeType !== 3) {
            node.remove();
        }
    });
}

function cleanElement(el) {
    let tag = el.tagName.toLowerCase();

    // Office-Namespaces: <o:p> enthält nur den Leerabsatz-Platzhalter, <v:*>/<w:*> sind VML/Word-Interna
    if (tag.includes(':')) {
        if (tag === 'o:p') {
            cleanChildren(el);
            unwrap(el);
        } else {
            el.remove();
        }
        return;
    }
    if (REMOVE_WITH_CONTENT.includes(tag) || tag === 'hr' || tag === 'col' || tag === 'colgroup') {
        el.remove();
        return;
    }

    const style = parseStyle(styleOf(el));

    // was in der Quelle unsichtbar war, soll weder sichtbar werden noch versteckt mitreisen
    if (style.display === 'none' || style.visibility === 'hidden' || style['mso-hide'] === 'all' || el.hasAttribute('hidden')) {
        el.remove();
        return;
    }

    if (/^h[1-6]$/.test(tag)) {
        // Überschriften werden fette Absätze in Hausschrift
        const strong = el.ownerDocument.createElement('strong');
        while (el.firstChild) {
            strong.appendChild(el.firstChild);
        }
        el.appendChild(strong);
        el = rename(el, 'p');
        tag = 'p';
    } else if (tag === 'pre') {
        Array.from(el.childNodes).forEach(child => {
            if (child.nodeType === 3 && child.textContent.includes('\n')) {
                const parts = child.textContent.split('\n');
                parts.forEach((part, index) => {
                    if (index > 0) {
                        el.insertBefore(el.ownerDocument.createElement('br'), child);
                    }
                    el.insertBefore(el.ownerDocument.createTextNode(part), child);
                });
                child.remove();
            }
        });
        el = rename(el, 'p');
        tag = 'p';
    } else if (tag === 'font') {
        const color = el.getAttribute('color');
        if (color && !style.color) {
            style.color = color;
        }
        el = rename(el, 'span');
        tag = 'span';
    } else if (tag === 'mark') {
        style['background-color'] = style['background-color'] || 'yellow';
        el = rename(el, 'span');
        tag = 'span';
    } else if (tag === 'caption') {
        el = rename(el, 'p');
        tag = 'p';
        const table = el.closest('table');
        if (table) {
            table.parentNode.insertBefore(el, table);
        }
    } else if (MARK_TAGS[tag]) {
        if (MARK_TAGS[tag] === 'strong' && /^(normal|[1-4]00)$/.test(style['font-weight'] || '')) {
            el = rename(el, 'span');
            tag = 'span';
        } else if (MARK_TAGS[tag] !== tag) {
            el = rename(el, MARK_TAGS[tag]);
            tag = MARK_TAGS[tag];
        }
    }

    if (tag === 'a') {
        const href = (el.getAttribute('href') || '').trim();
        if (!SAFE_LINK.test(href)) {
            cleanChildren(el);
            unwrap(el);
            return;
        }
    }
    if (tag === 'img') {
        const src = (el.getAttribute('src') || '').trim();
        if (!SAFE_IMAGE.test(src)) {
            el.remove();
            return;
        }
    }

    cleanChildren(el);

    if (!KEEP_TAGS.includes(tag) && !BLOCK_CONTAINERS.includes(tag)) {
        // unbekannte Inline-Elemente (small, sub, code, label, …): Inhalt behalten, Formatierung über span
        const allowed = allowedStyle(style, 'span');
        if (allowed) {
            el = rename(el, 'span');
            tag = 'span';
        } else {
            unwrap(el);
            return;
        }
    }

    setAttributes(el, tag, style);
}

function parseStyle(text) {
    const style = {};
    text.split(';').forEach(declaration => {
        const index = declaration.indexOf(':');
        if (index > 0) {
            style[declaration.slice(0, index).trim().toLowerCase()] = declaration.slice(index + 1).trim().replace(/\s*!important$/i, '');
        }
    });
    return style;
}

function backgroundOf(style) {
    if (style['mso-highlight']) {
        return style['mso-highlight'];
    }
    if (style['background-color']) {
        return style['background-color'];
    }
    if (style.background) {
        const color = style.background.match(/(#[0-9a-f]{3,6}\b|rgba?\([^)]*\)|\b[a-z]+\b)/i);
        return color && !/^(none|url|repeat|no)$/i.test(color[1]) ? color[1] : null;
    }
    return null;
}

function allowedStyle(style, tag) {
    const result = [];
    const weight = style['font-weight'];
    if (weight && (/^(bold|bolder)$/i.test(weight) || parseInt(weight, 10) >= 600)) {
        result.push('font-weight:bold');
    }
    if (/^(italic|oblique)/i.test(style['font-style'] || '')) {
        result.push('font-style:italic');
    }
    const decoration = (style['text-decoration'] || '') + ' ' + (style['text-decoration-line'] || '');
    const decorations = [];
    if (/underline/i.test(decoration)) {
        decorations.push('underline');
    }
    if (/line-through/i.test(decoration)) {
        decorations.push('line-through');
    }
    if (decorations.length) {
        result.push('text-decoration:' + decorations.join(' '));
    }

    const isInline = !BLOCK_TAGS.includes(tag);
    const isCell = tag === 'td' || tag === 'th';
    if (isInline) {
        result.push(...fontStyle(style));
        const color = normalizeColor(style.color);
        if (color) {
            result.push('color:' + color);
        }
    }
    if (isInline || isCell) {
        const background = normalizeBackground(backgroundOf(style));
        if (background) {
            result.push('background-color:' + background);
        }
    }
    if (!isInline && /^(center|right|justify)$/i.test(style['text-align'] || '')) {
        result.push('text-align:' + style['text-align'].toLowerCase());
    }
    return result.join(';');
}

function setAttributes(el, tag, style) {
    const spacing = ['data-space-before', 'data-space-after'];
    const keep = {
        a: ['href'],
        img: ['src', 'alt', 'width', 'height'],
        td: ['colspan', 'rowspan'],
        th: ['colspan', 'rowspan'],
        ol: ['start'],
    }[tag] || [];
    Array.from(el.attributes).forEach(attr => {
        if (!keep.includes(attr.name.toLowerCase()) && !spacing.includes(attr.name.toLowerCase())) {
            el.removeAttribute(attr.name);
        }
    });
    if (tag === 'img') {
        ['width', 'height'].forEach(name => {
            const value = el.getAttribute(name);
            if (value && !/^\d+$/.test(value)) {
                el.removeAttribute(name);
            }
        });
    }
    const allowed = allowedStyle(style, tag);
    if (allowed) {
        el.setAttribute('style', allowed);
    }

    // Farbe/Schrift am Absatz/an der Zelle: der Editor kennt sie nur als Textauszeichnung → auf den Inhalt legen
    if (BLOCK_TAGS.includes(tag) && el.textContent.trim()) {
        const color = normalizeColor(style.color);
        const blockStyle = [...fontStyle(style), ...(color ? ['color:' + color] : [])];
        if (blockStyle.length) {
            wrapInlineRuns(el, blockStyle.join(';'));
        }
    }
}

function fontStyle(style) {
    if (!keepFormatting) {
        return [];
    }
    const result = [];
    const family = (style['font-family'] || '').trim();
    if (family && !isHouseFont(family) && !/^(inherit|initial)$/i.test(family)) {
        result.push('font-family:' + family.replace(/"/g, "'"));
    }
    const size = normalizeFontSize(style['font-size']);
    if (size) {
        result.push('font-size:' + size);
    }
    return result;
}

function wrapInlineRuns(el, style) {
    let run = [];
    const flush = before => {
        if (run.length) {
            const span = el.ownerDocument.createElement('span');
            span.setAttribute('style', style);
            el.insertBefore(span, before);
            run.forEach(node => span.appendChild(node));
        }
        run = [];
    };
    Array.from(el.childNodes).forEach(child => {
        if (child.nodeType === 1 && BLOCK_TAGS.includes(child.tagName.toLowerCase())) {
            flush(child);
        } else {
            run.push(child);
        }
    });
    flush(null);
}

// div & Co.: mit Block-Inhalt auflösen, sonst zum Absatz machen
function outsideExactTables(el) {
    return !el.closest('[data-email-block]');
}

function normalizeBlocks(root) {
    Array.from(root.querySelectorAll(BLOCK_CONTAINERS.join(','))).filter(outsideExactTables).reverse().forEach(el => {
        if (!el.parentNode) {
            return;
        }
        const hasBlockChild = Array.from(el.children).some(child => BLOCK_TAGS.includes(child.tagName.toLowerCase()));
        if (hasBlockChild) {
            // lose Inline-Teile neben Blöcken in eigene Absätze packen
            let pending = [];
            const flush = before => {
                if (pending.some(node => node.nodeType === 1 || node.textContent.trim())) {
                    const p = el.ownerDocument.createElement('p');
                    pending.forEach(node => p.appendChild(node));
                    el.insertBefore(p, before);
                }
                pending = [];
            };
            Array.from(el.childNodes).forEach(child => {
                if (child.nodeType === 1 && BLOCK_TAGS.includes(child.tagName.toLowerCase())) {
                    flush(child);
                } else {
                    pending.push(child);
                }
            });
            flush(null);
            unwrap(el);
        } else {
            rename(el, 'p');
        }
    });

    // <div>Text<br></div> bzw. <div><br></div> (Outlook im Web, neues Outlook, Gmail): das letzte <br> eines Blocks
    // zeigt der Browser nicht an – im Editor wäre es ein echter Umbruch und die Zeile doppelt so hoch
    Array.from(root.querySelectorAll('p, li, td, th')).filter(outsideExactTables).forEach(removeTrailingBreak);
    removeBreaksBeforeBlocks(root);
    applyParagraphSpacing(root);
    splitLinesIntoParagraphs(root);

    // Word-Leerabsatz (<p>&nbsp;</p>) als echter leerer Absatz
    Array.from(root.querySelectorAll('p')).filter(outsideExactTables).forEach(p => {
        if (!p.querySelector('img, br') && !p.textContent.replace(/[\s\u00a0]/g, '')) {
            p.innerHTML = '';
        }
    });
}

// Geladener Inhalt (Vorlagen, alte TinyMCE-Entwürfe): "Text<br>" am Blockende wie beim Einfügen behandeln
export function stripTrailingBreaks(html) {
    if (!html || !/<br|&nbsp;| /i.test(html)) {
        return html;
    }
    const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
    [body, ...body.querySelectorAll('p, li, td, th, div')]
        .filter(block => !block.closest('[data-twig]'))
        .forEach(removeTrailingBreak);
    removeBreaksBeforeBlocks(body);
    splitLinesIntoParagraphs(body);

    // Leerzeile <p>&nbsp;</p> (so speichert emailHtml.js sie) als echter leerer Absatz – sonst steht das
    // &nbsp; vor dem Text, den man dort tippt
    Array.from(body.querySelectorAll('p'))
        .filter(p => !p.closest('[data-twig], table'))
        .forEach(p => {
            if (!p.querySelector('img, br') && !p.textContent.replace(/[\s ]/g, '')) {
                p.innerHTML = '';
            }
        });
    return body.innerHTML;
}

// Wie Outlook: jede Zeile ein eigener Absatz. Vorlagen sind "Hallo,<br><br>Text<br>…" – als ein Absatz mit
// Umbrüchen würde eine Aufzählung den ganzen Block erfassen bzw. auf einer leeren ersten Zeile beginnen.
// Optisch gleich: Absätze haben keinen Abstand, jedes <br> wird eine Absatzgrenze, Leerzeilen leere Absätze.
// Inhalt eines Absatzes an seinen <br> in Zeilen-Fragmente teilen; umschließende Inline-Elemente
// (<span style>, <strong>, <a>) werden für jede Zeile mitkopiert. Ohne Range-API (fehlt im Test-jsdom).
function splitAtBreaks(block) {
    const doc = block.ownerDocument;
    const newLine = () => ({root: doc.createDocumentFragment(), path: []});
    const lines = [newLine()];

    const append = (node, wrappers) => {
        const line = lines[lines.length - 1];
        let common = 0;
        while (common < line.path.length && common < wrappers.length && line.path[common].original === wrappers[common]) {
            common++;
        }
        line.path.length = common;
        wrappers.slice(common).forEach(original => {
            const parent = line.path.length ? line.path[line.path.length - 1].copy : line.root;
            const copy = parent.appendChild(original.cloneNode(false));
            line.path.push({original, copy});
        });
        (line.path.length ? line.path[line.path.length - 1].copy : line.root).appendChild(node);
    };

    const walk = (node, wrappers) => {
        Array.from(node.childNodes).forEach(child => {
            if (child.nodeType === 1 && child.tagName === 'BR' && !child.closest('[data-twig]')) {
                lines.push(newLine());
            } else if (child.nodeType === 1 && !child.hasAttribute('data-twig') && child.querySelector('br')) {
                walk(child, [...wrappers, child]);
            } else {
                append(child.cloneNode(true), wrappers);
            }
        });
    };
    walk(block, []);
    return lines.map(line => line.root);
}

export function splitLinesIntoParagraphs(root) {
    const doc = root.ownerDocument;
    const skip = el => el.closest('li, table, [data-twig], [data-email-block], blockquote');

    // lose Inline-Teile direkt im Container (Vorlagen-Text) in Absätze packen
    let run = [];
    const flush = before => {
        if (run.some(node => node.nodeType === 1 || node.textContent.trim())) {
            const p = doc.createElement('p');
            root.insertBefore(p, before);
            run.forEach(node => p.appendChild(node));
        }
        run = [];
    };
    Array.from(root.childNodes).forEach(child => {
        if (child.nodeType === 1 && BLOCK_TAGS.includes(child.tagName.toLowerCase())) {
            flush(child);
        } else {
            run.push(child);
        }
    });
    flush(null);

    Array.from(root.querySelectorAll('p')).filter(p => !skip(p) && p.querySelector('br')).forEach(p => {
        const breaks = Array.from(p.querySelectorAll('br')).filter(br => !br.closest('[data-twig]'));
        if (!breaks.length) {
            return;
        }
        const parts = splitAtBreaks(p);
        parts.forEach(part => {
            const paragraph = p.cloneNode(false);
            // Hüllen ohne Text (z.B. leeres <span style> vom Teilen) zählen als leere Zeile
            if (part.textContent.replace(/[\s\u00a0]/g, '') || part.querySelector('img, [data-twig]')) {
                paragraph.appendChild(part);
            }
            p.parentNode.insertBefore(paragraph, p);
        });
        p.remove();
    });
}

// "Text:<br><br><table>": das <br> direkt vor einem Block zeigt der Browser nicht an (Text + eine Leerzeile).
// Im Editor würde der Absatz auf zwei Umbrüche enden → zwei Leerzeilen. Also wie der Browser: eins weg.
export function removeBreaksBeforeBlocks(root) {
    Array.from(root.querySelectorAll('table, p, ul, ol, div, h1, h2, h3, h4, h5, h6'))
        .filter(block => !block.closest('[data-twig]') && !(block.parentElement && block.parentElement.closest('table')))
        .forEach(block => {
            let node = block.previousSibling;
            while (node && node.nodeType === 3 && /^[ \t\r\n]*$/.test(node.textContent)) {
                node = node.previousSibling;
            }
            // auch <span>Text<br></span><table>
            while (node && node.nodeType === 1 && node.tagName !== 'BR' && !BLOCK_TAGS.includes(node.tagName.toLowerCase()) && node.lastChild) {
                node = node.lastChild;
                while (node && node.nodeType === 3 && /^[ \t\r\n]*$/.test(node.textContent)) {
                    node = node.previousSibling;
                }
            }
            if (node && node.nodeType === 1 && node.tagName === 'BR') {
                node.remove();
            }
        });
}

function removeTrailingBreak(block) {
    let node = block.lastChild;
    while (node) {
        // nur echter Leerraum – &nbsp; ist Inhalt (so kennzeichnet emailHtml eine gewollte Leerzeile)
        if (node.nodeType === 3 && /^[ \t\r\n]*$/.test(node.textContent)) {
            node = node.previousSibling;
        } else if (node.nodeType === 1 && node.tagName === 'BR') {
            node.remove();
            return;
        } else if (node.nodeType === 1 && !BLOCK_TAGS.includes(node.tagName.toLowerCase()) && node.lastChild) {
            // <span>Text<br></span>
            node = node.lastChild;
        } else {
            return;
        }
    }
}

function removeEmptyInlines(root) {
    Array.from(root.querySelectorAll('span, strong, em, u, s, a')).filter(outsideExactTables).reverse().forEach(el => {
        if (!el.childNodes.length) {
            el.remove();
        } else if (el.tagName.toLowerCase() === 'span' && !el.getAttribute('style')) {
            unwrap(el);
        }
    });
}
